import {
  amber,
  blue,
  blueGrey,
  brown,
  cyan,
  deepOrange,
  deepPurple,
  green,
  grey,
  indigo,
  lightBlue,
  lightGreen,
  lime,
  orange,
  pink,
  purple,
  red,
  teal,
  yellow,
} from '@material-ui/core/colors';
import { SupportedLanguage } from 'humanize-duration';
import countries from 'i18n-iso-countries';
import i18n from 'i18next';
import UserRole from 'src/types/UserRoles';

import { Tax } from '../types/tax';

import {
  Axle,
  BidAutomationType,
  BidFilterStatus,
  BidSuccessStatus,
  BidVisibility,
  BuyableStatus,
  CarDetailing,
  ConditionalFormattingScope,
  ConditionalFormattingType,
  DamageCategory,
  DamageType,
  DateFilter,
  DeliveryAddress,
  DeliveryAddressResponse,
  DeliveryAddressType,
  DeliveryStatus,
  EquipmentCategory,
  MarketingChannel,
  NormalizedFuel,
  NormalizedFuelDetail,
  NormalizedTransmission,
  OfferType,
  PriceType,
  ReasonDto,
  RecommendedAction,
  RimType,
  Side,
  SourceType,
  TaxType,
  TermsConditionsType,
  TireType,
  TransportationProvider,
  TransportationSelectionType,
  TransportationState,
  UsernameStatusBasic,
  ValidityType,
  ValuationCountryCode,
} from './generated/api';
import { PROVIDERS_SHOWN_AS_VIA_ALLMOBIL } from './global-vars';
import { Option, tr } from './i18n-helpers';
import { capitalizeWords } from './string-helpers';
import { CARS_BIDS_TABLE_COLUMNS, CarTableColumn } from './table-data';
import { SUPPORTED_COUNTRIES } from './valuation-country-helpers';

export const getLabel = (options: Readonly<Option[]>, value: string): string => {
  const option = options.find(({ value: itemValue }) => itemValue === value);
  return option ? tr(option.label, option.options) : capitalizeWords(value ?? '');
};

export const sortOptionsByLabel = (options: Readonly<Option[]>): Option[] =>
  [...options].sort((a, b) => tr(a.label, a.options).localeCompare(tr(b.label, b.options), i18n.language));

export const colors: Readonly<Option[]> = [
  { value: 'BG', label: 'colors.beige' },
  { value: 'BL', label: 'colors.blue' },
  { value: 'BR', label: 'colors.brown' },
  { value: 'GE', label: 'colors.yellow' },
  { value: 'GN', label: 'colors.green' },
  { value: 'GO', label: 'colors.gold' },
  { value: 'GR', label: 'colors.grey' },
  { value: 'OR', label: 'colors.orange' },
  { value: 'RT', label: 'colors.red' },
  { value: 'SI', label: 'colors.silver' },
  { value: 'SW', label: 'colors.black' },
  { value: 'WS', label: 'colors.white' },
];

export const highlightLabels: Readonly<Option[]> = [
  { value: 'COLOR', label: 'colors.color' },
  { value: 'COUNTRY', label: 'car.countryOrigin' },
  { value: 'EQUIPMENT', label: 'car.equipment' },
  { value: 'ASSESSMENT', label: 'common.assessment' },
  { value: 'DAMAGE_TYPE', label: 'car.damage' },
  { value: 'VIRTUAL_SOURCE', label: 'common.virtual_source' },
];

export const recommendedActions: Readonly<Option[]> = [
  { value: RecommendedAction.Buy, label: 'recommendationsPage.card.actions.buy' },
  { value: RecommendedAction.LowSupply, label: 'recommendationsPage.card.actions.lowSupply' },
];

export const marketingChannel: Readonly<Option[]> = [
  {
    label: 'carsTableNext.filter.marketingChannels.FIXED',
    value: MarketingChannel.Fixed,
  },
  {
    label: 'carsTableNext.filter.marketingChannels.BID',
    value: MarketingChannel.Bid,
  },
];

type EquipmentOption = Option & { filterOnly: boolean };

export const equipments: Readonly<EquipmentOption[]> = [
  { value: 'AIRSUSP', label: 'car.equipments.airSuspension', filterOnly: false },
  { value: 'AWD', label: 'car.equipments.allWheel', filterOnly: false },
  { value: 'ALU', label: 'car.equipments.aluWheel_plural', filterOnly: false },
  { value: 'AHK', label: 'car.equipments.towCoupling', filterOnly: false },
  { value: 'WARRANTY_EXTENSION', label: 'car.equipments.warrantyExt', filterOnly: false },
  { value: 'ASSI', label: 'car.equipments.assistance', filterOnly: false },
  { value: 'AUT', label: 'car.equipments.automatic', filterOnly: false },
  { value: 'BUSINESS', label: 'car.equipments.businessPackage', filterOnly: false },
  { value: 'COMFORT', label: 'car.equipments.comfort', filterOnly: false },
  { value: 'FRLINE', label: 'car.equipments.frline', filterOnly: false },
  { value: 'INFOTAINMENT', label: 'car.equipments.infotainment', filterOnly: false },
  { value: 'AC', label: 'car.equipments.airCond', filterOnly: false },
  { value: 'ACAUTO', label: 'car.equipments.autoAirCond', filterOnly: false },
  { value: 'LEDER', label: 'car.equipments.leather', filterOnly: false },
  { value: 'TRUCKREG', label: 'car.equipments.truckPerm', filterOnly: false },
  { value: 'NAVI', label: 'car.equipments.navSys', filterOnly: false },
  { value: 'OHNEZBTI', label: 'promotions.OHNEZBTI', filterOnly: false },
  { value: 'PDC', label: 'car.equipments.parkDistCont', filterOnly: false },
  { value: 'RAD2', label: 'car.equipments.rad2', filterOnly: false },
  { value: 'REIFEN2', label: 'car.equipments.reifen2', filterOnly: false },
  { value: 'RLINE', label: 'car.equipments.rLine', filterOnly: false },
  { value: 'REARCAM', label: 'car.equipments.rearCam', filterOnly: false },
  { value: 'SLINE', label: 'car.equipments.sLine', filterOnly: false },
  { value: 'ESD', label: 'car.equipments.sunPanRoof', filterOnly: false },
  { value: 'SHZ', label: 'car.equipments.seatHeater', filterOnly: false },
  { value: 'SONDER', label: 'car.equipments.specialInstallation', filterOnly: false },
  { value: 'SOUND', label: 'car.equipments.soundSystem', filterOnly: false },
  { value: 'SPORTLINE', label: 'car.equipments.sportline', filterOnly: false },
  { value: 'SPORTS_PACKAGE', label: 'car.equipments.sports_package', filterOnly: false },
  { value: 'STDHZG', label: 'car.equipments.stationaryHeating', filterOnly: false },
  { value: 'TEL', label: 'car.equipments.speakerphone', filterOnly: false },
  { value: 'GRA', label: 'car.equipments.cruiseControl', filterOnly: false },
  { value: 'HEAT_PUMP', label: 'car.equipments.heatPump', filterOnly: false },
  { value: 'WINTER', label: 'car.equipments.winterPackage', filterOnly: false },
  { value: 'XENON', label: 'car.equipments.xenon', filterOnly: false },
  { value: 'VIRTUAL_COCKPIT', label: 'car.equipments.virtualCockpit', filterOnly: false },
  { value: 'APPLE_CAR_PLAY', label: 'car.equipments.appleCarPlay', filterOnly: false },
  { value: 'ANDROID_AUTO', label: 'car.equipments.androidAuto', filterOnly: false },
  { value: 'SEAT_HEATING_FOR_FRONT_AND_REAR_SEATS', label: 'car.equipments.seatHeaterFrontRear', filterOnly: false },
  { value: 'SEAT_HEATING_FOR_FRONT_SEATS', label: 'car.equipments.seatHeaterFront', filterOnly: false },
  { value: 'PANORAMIC_SUNROOF', label: 'car.equipments.panoramicSunroof', filterOnly: false },
  { value: 'AUTOMATIC_CRUISE_CONTROL', label: 'car.equipments.adaptiveCruiseControl', filterOnly: false },
  { value: 'HEAD_UP_DISPLAY', label: 'car.equipments.headUpDisplay', filterOnly: false },
  { value: 'AREA_VIEW_360', label: 'car.equipments.areaView360', filterOnly: false },
  { value: 'MATRIX_LED_HEADLIGHTS', label: 'car.equipments.matrixLedHeadlights', filterOnly: false },
  { value: 'PARTIAL_LEATHER', label: 'car.equipments.partialLeather', filterOnly: true },
  { value: 'PARK_ASSIST', label: 'car.equipments.parkAssist', filterOnly: true },
  { value: 'EXIT_PARKING_ASSIST', label: 'car.equipments.exitParkingAssist', filterOnly: true },
  { value: 'LANE_CHANGE_ASSIST', label: 'car.equipments.laneChangeAssist', filterOnly: true },
  { value: 'LANE_KEEPING_ASSIST', label: 'car.equipments.laneKeepingAssist', filterOnly: true },
  { value: 'HIGH_BEAM_ASSIST', label: 'car.equipments.highBeamAssist', filterOnly: true },
  { value: 'TRAFFIC_JAM_ASSIST', label: 'car.equipments.trafficJamAssist', filterOnly: true },
  { value: 'TRAILER_ASSIST', label: 'car.equipments.trailerAssist', filterOnly: true },
  { value: 'DIGITAL_RADIO', label: 'car.equipments.digitalRadio', filterOnly: true },
  { value: 'THIRD_ROW_SEATS', label: 'car.equipments.thirdRowSeats', filterOnly: true },
  { value: 'ELECTRIC_TAILGATE', label: 'car.equipments.electricTailgate', filterOnly: true },
  { value: 'HEATED_STEERING_WHEEL', label: 'car.equipments.heatedSteeringWheel', filterOnly: true },
  { value: 'LIGHT_ALLOY_WHEELS', label: 'car.equipments.lightAlloyWheels', filterOnly: true },
  { value: 'PARKING_AID', label: 'car.equipments.parkingAid', filterOnly: true },
  { value: 'AIR_CONDITIONING', label: 'car.equipments.airConditioningVwfs', filterOnly: true },
  { value: 'ELECTRIC_SUNROOF', label: 'car.equipments.electricSunroof', filterOnly: true },
  { value: 'VWFS_SPORTS_PACKAGE', label: 'car.equipments.sportsPackageVwfs', filterOnly: true },
];

export const filterOnlyEquipments: readonly string[] = equipments
  .filter(({ filterOnly }) => filterOnly)
  .map(({ value }) => value);

export const displayableEquipments: Readonly<Option[]> = equipments.filter(({ filterOnly }) => !filterOnly);

export const equipmentCategories: Readonly<Option[]> = [
  { value: EquipmentCategory.BasicEquipment, label: 'car.equipmentCategories.basic' },
  { value: EquipmentCategory.SpecialEquipment, label: 'car.equipmentCategories.special' },
  { value: EquipmentCategory.PackageEquipment, label: 'car.equipmentCategories.package' },
  { value: 'other', label: 'car.equipmentCategories.other' },
];

export const specialHighlights: Readonly<Option[]> = [{ value: 'THERMO', label: 'car.equipments.thermo' }];

export const damageTypes: Readonly<Option[]> = [
  { value: DamageType.Hail, label: 'car.damageTypes.hail' },
  { value: DamageType.Accident, label: 'car.damageTypes.accident' },
  { value: DamageType.Dirt, label: 'car.damageTypes.dirt' },
  { value: DamageType.MissingWheelSet, label: 'car.damageTypes.missingWheelset' },
  { value: DamageType.RepairedDamage, label: 'car.damageTypes.repairedDamage' },
  { value: DamageType.HighDamage, label: 'car.damageTypes.highDamage' },
  { value: DamageType.Refurbished, label: 'car.damageTypes.refurbished' },
  { value: DamageType.NonRollableNorDriveable, label: 'car.damageTypes.nonRollableNorDriveable' },
];

export const assessmentDocumentTypes: Readonly<Option[]> = [
  { value: 'PRIMARY', label: 'common.assessment' },
  { value: 'WORKSHOP_REPORT', label: 'common.workshopReport' },
  { value: 'PRIOR_DAMAGE_REPORT', label: 'common.priorDamageReport' },
  { value: 'OTHER', label: 'common.otherDocument' },
];

export const damageCategories: Readonly<Option<DamageCategory>[]> = [
  { value: DamageCategory.AccidentDamage, label: 'car.damageCategories.accidentDamage' },
  { value: DamageCategory.UsageTrace, label: 'car.damageCategories.usageTrace' },
  { value: DamageCategory.MissingPart, label: 'car.damageCategories.missingPart' },
  { value: DamageCategory.Other, label: 'car.damageCategories.other' },
];

export const taxationTypes: Readonly<Option[]> = [
  { value: TaxType.Regelbesteuert, label: 'carsTable.regularilyTaxed' },
  { value: TaxType.Differenzbesteuert, label: 'carsTable.differentialTaxation' },
];

export const taxes: Readonly<Option<Tax>[]> = [
  { value: Tax.VAT, label: 'tax.VAT' },
  { value: Tax.NOVA, label: 'tax.NOVA' },
  { value: Tax.REGISTRATION, label: 'tax.REGISTRATION' },
  { value: Tax.ECO, label: 'tax.ECO' },
  { value: Tax.ECOMALUS, label: 'tax.ECOMALUS' },
  { value: Tax.TRANSCRIPTION, label: 'tax.TRANSCRIPTION' },
  // { value: 'AUTHENTICATION', label: 'tax.AUTHENTICATION' },
  // { value: 'PROPERTY_ACQUISITION', label: 'tax.PROPERTY_ACQUISITION' },
  // { value: 'CIVIL', label: 'tax.CIVIL' },
  // { value: 'EXCISE', label: 'tax.EXCISE' },
];

export const taxesPerCountry: Record<ValuationCountryCode, Array<Tax>> = {
  AT: [Tax.NOVA, Tax.VAT],
  AT_LOT: [Tax.NOVA, Tax.VAT],
  DE: [Tax.VAT],
  ES: [Tax.REGISTRATION, Tax.VAT],
  FR: [Tax.ECO, Tax.VAT],
  IT: [Tax.ECOMALUS, Tax.TRANSCRIPTION, Tax.VAT],
  CZ: [Tax.REGISTRATION, Tax.VAT],
  HU: [Tax.AUTHENTICATION, Tax.PROPERTY_ACQUISITION, Tax.REGISTRATION, Tax.VAT],
  PL: [Tax.CIVIL, Tax.EXCISE, Tax.VAT],
  RO: [Tax.VAT],
  SI: [Tax.REGISTRATION, Tax.VAT],
  HR: [Tax.REGISTRATION, Tax.VAT],
  SK: [Tax.VAT],
  PT: [Tax.REGISTRATION, Tax.VAT],
  SE: [Tax.VAT],
  BG: [Tax.VAT],
  MK: [Tax.VAT],
  AL: [Tax.VAT],
  XK: [Tax.VAT],
  NL: [Tax.AGE_DISCOUNT, Tax.GROSS_BPM, Tax.VAT],
  BE: [Tax.REGISTRATION, Tax.RESIDUAL_VALUE, Tax.VAT],
  NO: [Tax.AGE_DISCOUNT, Tax.CO2, Tax.WEIGHT, Tax.VAT],
  DK: [Tax.REGISTRATION, Tax.VAT],
  CH: [Tax.AUTOMOBILSTEUER, Tax.VAT],
};

export const sources: Readonly<Option<SourceType>[]> = [
  { value: SourceType.AudiGws, label: 'source.audiGws' },
  { value: SourceType.AudiUcp, label: 'Audi UCP' },
  { value: SourceType.Autobid, label: 'Autobid' },
  { value: SourceType.Autorola, label: 'Autorola' },
  { value: SourceType.Auto1, label: 'Auto1' },
  { value: SourceType.Okcars, label: 'OK Cars' },
  // { value: SourceType.Vdv3, label: 'VDV3' },
  { value: SourceType.Vwfs, label: 'VWFS' },
  // { value: SourceType.VwfsDeAgb, label: 'VWFS DE Augsburg' },
  // { value: SourceType.VwfsDeFraAudi, label: 'VWFS DE Frankfurt Audi' },
  // { value: SourceType.VwfsDeFraVw, label: 'VWFS DE Frankfurt VW' },
  // { value: SourceType.VwfsDeStg, label: 'VWFS DE Stuttgart' },
  // { value: SourceType.VwfsFr, label: 'VWFS Frankreich' },
  { value: SourceType.VwfsItVirt, label: 'source.vwfsItVirt' },
  { value: SourceType.VwfsSkVirt, label: 'source.vwfsSkVirt' },
  { value: SourceType.VwfsDkVirt, label: 'source.vwfsDkVirt' },
  { value: SourceType.VwfsPtVirt, label: 'source.vwfsPtVirt' },
  { value: SourceType.VwfsFrVirt, label: 'source.vwfsFrVirt' },
  { value: SourceType.VwfsCzVirt, label: 'source.vwfsCzVirt' },
  { value: SourceType.VwnGws, label: 'VWN GWS' },
  { value: SourceType.Arval, label: 'ARVAL' },
  { value: SourceType.RealFirstCallOwn, label: 'source.realFirstCallOwn' },
  { value: SourceType.RealFirstCallOther, label: 'source.realFirstCallOther' },
  { value: SourceType.Ucs, label: 'source.ucs' },
  { value: SourceType.Vvj, label: 'VVJ' },
  { value: SourceType.SpotdealsAllmobil, label: 'Spotdeals Allmobil' },
  { value: SourceType.Ppn, label: 'Porsche B2B' },
];

export const offerTypes: Readonly<Option[]> = [
  { value: OfferType.All, label: 'filterSearch.offerTypeAll' },
  { value: OfferType.Individual, label: 'filterSearch.offerTypeIndiv' },
  { value: OfferType.Package, label: 'common.package_plural' },
];

export const buyableStatus: Readonly<Option[]> = [
  { value: BuyableStatus.All, label: 'buyableStatus.all' },
  { value: BuyableStatus.Buyable, label: 'buyableStatus.buyable' },
  { value: BuyableStatus.NonBuyable, label: 'buyableStatus.nonBuyable' },
];

export const buyableStatusV2: Readonly<Option[]> = [
  { value: BuyableStatus.Buyable, label: 'buyableStatus.buyable' },
  { value: BuyableStatus.NonBuyable, label: 'buyableStatus.nonBuyable' },
];

export const promotions: Readonly<Option[]> = [
  { value: 'CLUSTER', label: 'promotions.cluster' },
  { value: 'DIRECT_OFFER', label: 'promotions.directOffer' },
  { value: 'EA189', label: 'promotions.eA189' },
  { value: 'EXKL.BONUS', label: 'promotions.exclusive' },
  { value: 'FIRST_CALL', label: 'promotions.firstCall' },
  { value: 'FIRSTCALL', label: 'promotions.firstCall' },
  { value: 'REAL_FIRST_CALL', label: 'promotions.realFirstCall' },
  { value: 'REAL_FIRST_CALL_OTHER', label: 'source.realFirstCallOther' },
  { value: 'GW-KENNZ', label: 'promotions.usedCar' },
  { value: 'HIGHLIGHT', label: 'promotions.highlight' },
  { value: 'VAL.10VVJ', label: 'promotions.10vvj' },
  { value: 'VAL.100VVJ', label: 'promotions.100vvj' },
  { value: 'VAL.40VVJ', label: 'promotions.40vvj' },
  { value: 'VAL.90VTI', label: 'promotions.90vti' },
  { value: 'VAL.90VVJ', label: 'promotions.90vvj' },
  { value: 'TRANSPORT', label: 'promotions.transport' },
  { value: 'VERMIETER', label: 'promotions.rentalCar' },
  { value: 'VOL.BONUS', label: 'promotions.volBonus' },
  { value: 'RESERVEABLE', label: 'promotions.reserveable' },
  { value: 'JEZ2022', label: 'promotions.jez2022' },
  { value: 'VAL.30SVK', label: 'promotions.val30svk' },
  { value: 'VAL.30VTI', label: 'promotions.val30vti' },
  { value: 'VAL.30VVJ', label: 'promotions.val30vvj' },
  { value: 'VAL.40VTI', label: 'promotions.val40vti' },
  { value: 'VOL.B.VTI', label: 'promotions.volb_vti' },
  { value: 'VAL.30SKO', label: 'promotions.val30sko' },
  { value: 'VAL.90SKO', label: 'promotions.val90sko' },
  { value: 'VAL.90SEA', label: 'promotions.val90sea' },
  { value: 'WUI', label: 'promotions.wui' },
  { value: 'TAX_DEDUCTION', label: 'promotions.tax_deduction' },
  { value: 'HAS_PAIN_AGREEMENT', label: 'promotions.pain' },
  { value: 'CASHBACK', label: 'promotions.cashback' },
  { value: 'BONUS_MODELL', label: 'promotions.BONUS_MODELL' },
  { value: 'FOKUS_MODELL', label: 'promotions.FOKUS_MODELL' },
  { value: 'PHS_VVJ', label: 'promotions.PHS_VVJ' },
  { value: 'HEISSW_AKTION', label: 'promotions.HEISSW_AKTION' },
  { value: 'BLACKWEEK', label: 'promotions.black_week' },
  { value: 'PRAEMIE', label: 'promotions.PRAEMIE' },
  { value: 'STAFFELBON2025', label: 'promotions.STAFFELBON2025' },
  { value: 'STELLPLATZ', label: 'promotions.Stellplatz' },
  { value: 'OSTERAKTIO', label: 'promotions.OSTERAKTIO' },
  { value: 'EMOB.BONUS', label: 'promotions.EMOB' },
  { value: 'AFTERSHOW2025Q4', label: 'promotions.AFTERSHOW2025Q4' },
  { value: 'AFTERSHOW', label: 'promotions.AFTERSHOW' },
  { value: 'BIGBANG', label: 'promotions.AK_BIG' },
  { value: 'FLASH', label: 'promotions.FLASH' },
  { value: 'NOBONUS', label: 'promotions.NOBONUS' },
  { value: 'SMARTSPEND', label: 'promotions.SMARTSPEND' }, // golden autumn
  { value: 'SMARTSPEND_09_2026', label: 'promotions.SMARTSPEND_09_2026' }, // golden september
  { value: 'SMARTSPEND_Q2_2026', label: 'promotions.SMARTSPEND_Q2_2026' }, // old smart spend before sept 2026
  { value: 'SMARTSPEND2025', label: 'promotions.SMARTSPEND2025' }, // old Catch & Bid Promo
  { value: 'EXPORT', label: 'promotions.DISCOUNT' }, // a little confusing but the naming is correct here
  { value: 'OUTLET', label: 'promotions.OUTLET' },
  { value: 'YOUNG_SKO', label: 'promotions.YOUNGSKODA' },
  { value: 'SPARSCHW', label: 'promotions.PIGGYBANK' },
  { value: 'STAFFELBON', label: 'promotions.STAFFELBON' },
  { value: 'GWTURBO', label: 'promotions.VTI_GW_TURBO' },
  { value: 'FAKTURA2027', label: 'promotions.FAKTURA2027' }, // grüner stern
];

export const searchAgentSaveModes: Readonly<Option[]> = [
  { value: 'NEW', label: 'filterSearch.newSearchAgent' },
  { value: 'EDIT', label: 'filterSearch.editSearchAgent' },
];

export const validityTypes: Readonly<Option[]> = [
  { value: ValidityType.All, label: 'bids.validityTypes.all' },
  { value: ValidityType.Current, label: 'bids.validityTypes.current' },
  { value: ValidityType.Expired, label: 'bids.validityTypes.expired' },
];

export const validityTypesV2: Readonly<Option[]> = [
  { value: ValidityType.Current, label: 'bids.validityTypes.current' },
  { value: ValidityType.Expired, label: 'bids.validityTypes.expired' },
];

export const validityTypesV2Dealer: Readonly<Option[]> = [
  { value: ValidityType.Current, label: 'bids.validityTypes.current' },
  { value: ValidityType.Expired, label: 'bids.validityTypes.expiredDealer' },
];

export const bidStatusTypes: Readonly<Option[]> = [
  { value: BidFilterStatus.All, label: 'filterSearch.offerTypeAll' },
  { value: BidFilterStatus.Bidden, label: 'bids.bidHammers.bidden' },
  { value: BidFilterStatus.NotBidden, label: 'bids.bidHammers.notBid' },
  { value: BidFilterStatus.AnybodyBidden, label: 'bids.bidHammers.biddenAnybody' },
  { value: BidFilterStatus.NobodyBidden, label: 'bids.bidHammers.notBiddenAnyone' },
];

export const bidStatusTypesV2: Readonly<Option[]> = [
  { value: BidFilterStatus.Bidden, label: 'bids.bidHammers.bidden' },
  { value: BidFilterStatus.NotBidden, label: 'bids.bidHammers.notBid' },
  { value: BidFilterStatus.AnybodyBidden, label: 'bids.bidHammers.biddenAnybody' },
  { value: BidFilterStatus.NobodyBidden, label: 'bids.bidHammers.notBiddenAnyone' },
];

export const getBidStatusTypes = (roles: UserRole[]) => {
  if (roles.includes(UserRole.ADMIN)) {
    return bidStatusTypesV2;
  }
  return bidStatusTypesV2.filter(
    (type) => type.value !== BidFilterStatus.AnybodyBidden && type.value !== BidFilterStatus.NobodyBidden,
  );
};

export const bidStatusTypesExpired: Readonly<Option[]> = [
  { value: BidFilterStatus.All, label: 'filterSearch.offerTypeAll' },
  { value: BidFilterStatus.Bidden, label: 'bids.bidHammers.expired.bidden' },
  { value: BidFilterStatus.NotBidden, label: 'bids.bidHammers.notBid' },
  { value: BidFilterStatus.AnybodyBidden, label: 'bids.bidHammers.biddenAnybody' },
  { value: BidFilterStatus.NobodyBidden, label: 'bids.bidHammers.notBiddenAnyone' },
];

export const bidStatusTypesExpiredV2: Readonly<Option[]> = [
  { value: BidFilterStatus.Bidden, label: 'bids.bidHammers.expired.bidden' },
  { value: BidFilterStatus.NotBidden, label: 'bids.bidHammers.notBid' },
  { value: BidFilterStatus.AnybodyBidden, label: 'bids.bidHammers.biddenAnybody' },
  { value: BidFilterStatus.NobodyBidden, label: 'bids.bidHammers.notBiddenAnyone' },
];

export const getBidStatusTypesExpired = (roles: UserRole[]) => {
  if (roles.includes(UserRole.ADMIN)) {
    return bidStatusTypesExpiredV2;
  }
  return bidStatusTypesExpiredV2.filter(
    (type) => type.value !== BidFilterStatus.AnybodyBidden && type.value !== BidFilterStatus.NobodyBidden,
  );
};

export const bidSuccessStatusTypes: Readonly<Option[]> = [
  { value: BidSuccessStatus.Highest, label: 'bids.status.highest' },
  { value: BidSuccessStatus.Outbid, label: 'bids.status.outbid' },
  { value: BidSuccessStatus.Sent, label: 'bids.status.sent' },
  { value: BidSuccessStatus.Cancelled, label: 'bids.status.cancelled' },
  { value: BidSuccessStatus.Error, label: 'bids.status.error' },
  { value: BidSuccessStatus.Bought, label: 'bids.status.bought' },
  { value: BidSuccessStatus.Scheduled, label: 'bids.status.scheduled' },
  { value: BidSuccessStatus.Allocate, label: 'bids.status.allocate' },
  { value: BidSuccessStatus.Deleted, label: 'bids.status.deleted' },
];

export const bidAutomationTypes: Readonly<Option[]> = [
  { value: BidAutomationType.Repeat, label: 'carsTableNext.filter.automatedBids.replace' },
  { value: BidAutomationType.PurchaseAgent, label: 'carsTableNext.filter.automatedBids.purchaseAgent' },
];

// Default value for the success states (bought & scheduled are not included)
export const bidSuccessStatusFilterable = [
  BidSuccessStatus.Highest,
  BidSuccessStatus.Outbid,
  BidSuccessStatus.Sent,
  BidSuccessStatus.Cancelled,
  BidSuccessStatus.Error,
  BidSuccessStatus.Allocate,
];

export const engineFuelOld: Readonly<Option[]> = [
  { value: NormalizedFuel.Petrol, label: 'car.engineFuelDetails.petrol' },
  { value: NormalizedFuel.Diesel, label: 'car.engineFuelDetails.diesel' },
  { value: NormalizedFuel.Elektro, label: 'car.engineFuelDetails.electric' },
  { value: NormalizedFuel.Hybrid, label: 'car.engineFuelDetails.hybrid' },
  { value: NormalizedFuel.PetrolHybrid, label: 'car.engineFuelDetails.petrol_hybrid' },
  { value: NormalizedFuel.DieselHybrid, label: 'car.engineFuelDetails.diesel_hybrid' },
  { value: NormalizedFuel.Gas, label: 'car.engineFuelDetails.gas' },
  { value: NormalizedFuel.GasHybrid, label: 'car.engineFuelDetails.gas_hybrid' },
  { value: NormalizedFuel.Hydrogen, label: 'car.engineFuelDetails.hydrogen' },
  { value: NormalizedFuel.Unknown, label: 'car.engineFuelDetails.other' },
];

export const engineFuelDetails: Readonly<Option[]> = [
  { value: NormalizedFuelDetail.Petrol, label: 'car.engineFuelDetails.petrol' },
  { value: NormalizedFuelDetail.Diesel, label: 'car.engineFuelDetails.diesel' },
  { value: NormalizedFuelDetail.PetrolHybridMild, label: 'car.engineFuelDetails.petrol_hybrid_mild' },
  { value: NormalizedFuelDetail.PetrolHybridPlugin, label: 'car.engineFuelDetails.petrol_hybrid_plugin' },
  { value: NormalizedFuelDetail.DieselHybridMild, label: 'car.engineFuelDetails.diesel_hybrid_mild' },
  { value: NormalizedFuelDetail.DieselHybridPlugin, label: 'car.engineFuelDetails.diesel_hybrid_plugin' },
  { value: NormalizedFuelDetail.PetrolHybrid, label: 'car.engineFuelDetails.petrol_hybrid' },
  { value: NormalizedFuelDetail.Gas, label: 'car.engineFuelDetails.gas' },
  { value: NormalizedFuelDetail.GasCng, label: 'car.engineFuelDetails.gas_cng' },
  { value: NormalizedFuelDetail.GasHybrid, label: 'car.engineFuelDetails.gas_hybrid' },
  { value: NormalizedFuelDetail.Elektro, label: 'car.engineFuelDetails.electric' },
  { value: NormalizedFuelDetail.Hydrogen, label: 'car.engineFuelDetails.hydrogen' },
  { value: NormalizedFuelDetail.Unknown, label: 'car.engineFuelDetails.other' },
];

export const sides: Readonly<Option[]> = [
  { value: Side.Left, label: 'common.positions.left' },
  { value: Side.Right, label: 'common.positions.right' },
];

export const axles: Readonly<Option[]> = [
  { value: Axle.Front, label: 'common.positions.front' },
  { value: Axle.Rear, label: 'common.positions.rear' },
];

export const tireTypes: Readonly<Option[]> = [
  { value: TireType.Winter, label: 'car.tireTypes.winter' },
  { value: TireType.Summer, label: 'car.tireTypes.summer' },
  { value: TireType.AllSeason, label: 'car.tireTypes.allSeason' },
  { value: TireType.SpareWheel, label: 'car.tireTypes.spareWheel' },
];

export const rimTypes: Readonly<Option[]> = [
  { value: RimType.Aluminium, label: 'car.rimTypes.aluminium' },
  { value: RimType.Steel, label: 'car.rimTypes.steel' },
];

export const transmissions: Readonly<Option[]> = [
  { value: NormalizedTransmission.Manual, label: 'car.transmissionTypes.manual' },
  { value: NormalizedTransmission.Automat, label: 'car.transmissionTypes.automatic' },
];

export const enginePowerUnits: Readonly<Option[]> = [
  { value: 'kW', label: 'car.kW' }, // currently not used since it's always kW in all supported languages
  { value: 'HP', label: 'car.hp' },
];

// BE has not yet defined an enum for this field, so unknown values fall back to the raw string via getLabel()
export const stateOfHealthQueryTypes: Readonly<Option[]> = [
  { value: 'Readout', label: 'car.stateOfHealthQueryTypes.readout' },
  { value: 'Calculated', label: 'car.stateOfHealthQueryTypes.calculated' },
];

export const bodyTypes: Readonly<Option[]> = [
  { value: '4x4', label: 'car.bodyTypes.4x4' },
  { value: 'bus', label: 'car.bodyTypes.bus' },
  { value: 'cabrio', label: 'car.bodyTypes.cabrio' },
  { value: 'compact', label: 'car.bodyTypes.compact' },
  { value: 'coupe', label: 'car.bodyTypes.coupe' },
  { value: 'hatchback', label: 'car.bodyTypes.hatchback' },
  { value: 'panel van', label: 'car.bodyTypes.panelVan' },
  { value: 'panelVan', label: 'car.bodyTypes.panelVan' },
  { value: 'platform', label: 'car.bodyTypes.platform' },
  { value: 'sedan', label: 'car.bodyTypes.sedan' },
  { value: 'suv', label: 'car.bodyTypes.suv' },
  { value: 'wagon', label: 'car.bodyTypes.wagon' },
  { value: 'caravan', label: 'car.bodyTypes.caravan' },
];

export const CarDetailingTypes: Readonly<Option[]> = [
  { value: CarDetailing.NoDetailing, label: 'dealerConfig.carDetailing.noDetailing' },
  { value: CarDetailing.ServicePackage, label: 'dealerConfig.carDetailing.servicePkg' },
  { value: CarDetailing.DetailingPackage, label: 'dealerConfig.carDetailing.detailingPkg' },
  { value: CarDetailing.DetailingPackageDiscounted, label: 'dealerConfig.carDetailing.detailingPkgDiscount' },
  { value: CarDetailing.RepairPackage, label: 'dealerConfig.carDetailing.repairPkg' },
];

export enum PotentialFilterType {
  PotentialInternetPriceMin = 'potentialInternetPriceMin',
  PotentialInternetPriceAverage = 'potentialInternetPriceAverage',
  PotentialInternetPriceMinAverage = 'potentialInternetPriceMinAverage',
}

export const potentialFilters: Readonly<Option<PotentialFilterType>[]> = [
  { value: PotentialFilterType.PotentialInternetPriceMin, label: 'car.prices.potentialPriceMin' },
  { value: PotentialFilterType.PotentialInternetPriceAverage, label: 'car.prices.potentialPriceAverage' },
  { value: PotentialFilterType.PotentialInternetPriceMinAverage, label: 'car.prices.potentialPriceMinAverage' },
];

export const sourceOriginFilter: Readonly<Option<string>[]> = [
  // { value: 'VTI', label: 'carsTableNext.filter.originSources.ALL_VWFS' }, // group with only a single item
  {
    value: 'GFZ VW FS,GFZ VW,MIETFZG FS,MIETFZG VW,NW KZL VW,RUECK,VGSG RAC,WA-LEASING,WA-RUECK,WANDLUNG',
    label: 'carsTableNext.filter.originSources.ALL_VW_AG',
  },
  { value: 'SAD,SAD WANDL,', label: 'carsTableNext.filter.originSources.ALL_SKODA' },
  { value: 'SEAT,SEAT WANDL,', label: 'carsTableNext.filter.originSources.ALL_SEAT' },
  { value: 'ITAVWLRAC,VWFSItaly,', label: 'carsTableNext.filter.originSources.ALL_VWFS_IT' },
  { value: 'SVK Insolvencies,SVK Standard Lease Return', label: 'carsTableNext.filter.originSources.ALL_VWFS_SK' },
  // { value: 'EMG', label: 'carsTableNext.filter.originSources.ALL_RENTAL_EXT' }, // group with only a single item
  { value: 'DNK FLeas,DNK Lux,DNK OLeas', label: 'carsTableNext.filter.originSources.ALL_VW_DK' },
  { value: 'FRA all,FRA Employee leasing,FRA RAC', label: 'carsTableNext.filter.originSources.ALL_FR' },
  { value: 'PRT Auto Credit,PRT Fin. Leasing,PRT Op. Leasing', label: 'carsTableNext.filter.originSources.ALL_PT' },
  { value: 'PRT Fin. Leasing', label: 'carsTableNext.filter.originSources.PRT_FIN_LEASING' },
  { value: 'EMG', label: 'carsTableNext.filter.originSources.EMG' },
  { value: 'PRT Auto Credit', label: 'carsTableNext.filter.originSources.PRT_AUTO_CREDIT' },
  { value: 'PRT Op. Leasing', label: 'carsTableNext.filter.originSources.PRT_OP_LEASING' },
  { value: 'DNK FLeas', label: 'carsTableNext.filter.originSources.DNK_FLEAS' },
  { value: 'FRA all', label: 'carsTableNext.filter.originSources.FR_ALL' },
  { value: 'DNK OLeas', label: 'carsTableNext.filter.originSources.DNK_OLEAS' },
  { value: 'SEAT', label: 'carsTableNext.filter.originSources.SEAT' },
  { value: 'SEAT WANDL', label: 'carsTableNext.filter.originSources.SEAT_WANDL' },
  { value: 'SVK Standard Lease Return', label: 'carsTableNext.filter.originSources.SVK_STANDARD_LEASE_RETURN' },
  { value: 'SVK Insolvencies', label: 'carsTableNext.filter.originSources.SVK_INSOLVENCIES' },
  { value: 'VTI', label: 'carsTableNext.filter.originSources.VTI' },
  { value: 'DNK Lux', label: 'carsTableNext.filter.originSources.DNK_LUX' },
  { value: 'FRA RAC', label: 'carsTableNext.filter.originSources.FRA_RAC' },
  { value: 'VGSG RAC', label: 'carsTableNext.filter.originSources.VGSG_RAC' },
  { value: 'ITAVWLRAC', label: 'carsTableNext.filter.originSources.ITAVWLRAC' },
  { value: 'VWFSItaly', label: 'carsTableNext.filter.originSources.VWFSItaly' },
  { value: 'GFZ VW', label: 'carsTableNext.filter.originSources.GFZ_VW' },
  { value: 'GFZ VW FS', label: 'carsTableNext.filter.originSources.GFZ_VW_FS' },
  { value: 'MIETFZG VW', label: 'carsTableNext.filter.originSources.MIETFZG_VW' },
  { value: 'MIETFZG FS', label: 'carsTableNext.filter.originSources.MIETFZG_FS' },
  { value: 'NW KZL VW', label: 'carsTableNext.filter.originSources.NW_KZL_VW' },
  { value: 'WA-LEASING', label: 'carsTableNext.filter.originSources.WA-LEASING' },
  { value: 'WANDLUNG', label: 'carsTableNext.filter.originSources.WANDLUNG' },
  { value: 'RUECK', label: 'carsTableNext.filter.originSources.RUECK' },
  { value: 'WA-RUECK', label: 'carsTableNext.filter.originSources.WA_RUECK' },
  { value: 'FRA Employee leasing', label: 'carsTableNext.filter.originSources.FRA_EMPLOYEE_LEASING' },
  { value: 'SAD', label: 'carsTableNext.filter.originSources.SAD' },
  { value: 'SAD WANDL', label: 'carsTableNext.filter.originSources.SAD_WANDL' },
];

export const ORIGIN_SOURCE_GROUPS = [
  { value: 'allVTI', label: 'carsTableNext.filter.originSources.ALL_VWFS' },
  { value: 'allVWAG', label: 'carsTableNext.filter.originSources.ALL_VW_AG' },
  { value: 'allSkoda', label: 'carsTableNext.filter.originSources.ALL_SKODA' },
  { value: 'allSeat', label: 'carsTableNext.filter.originSources.ALL_SEAT' },
  { value: 'allVWFSItaly', label: 'carsTableNext.filter.originSources.ALL_VWFS_IT' },
  { value: 'allSlowakei', label: 'carsTableNext.filter.originSources.ALL_VWFS_SK' },
  { value: 'allEMG', label: 'carsTableNext.filter.originSources.ALL_RENTAL_EXT' },
  { value: 'allDNK', label: 'carsTableNext.filter.originSources.ALL_VW_DK' },
  { value: 'allFRA', label: 'carsTableNext.filter.originSources.ALL_FR' },
  { value: 'allPRT', label: 'carsTableNext.filter.originSources.ALL_PT' },
];

export const CARS_TABLE_EXPORT_LABELS: Record<string, string> = {
  rating: 'carsTable.userRating',
  userComment: 'carsTable.userComment',
  dateStart: 'carsTable.startingTime',
  carId: 'car.ID',
  countryOrigin: 'car.countryOrigin',
  colorId: 'colors.color',
  equipmentKeys: 'car.equipment',
  assessmentLink: 'common.assessment',
  priceDamage: 'car.damage',
  damageType: 'car.damageType',
  priceFixed: 'car.prices.fixedPrice',
  priceOrientation: 'car.prices.priceOrientation',
  engineCo2: 'car.co2',
  nova: 'car.nova',
  co2ApproximationMethod: 'car.co2Approx',
  engineTestingProcedure: 'car.co2Testing',
  vin: 'car.vin',
  packageId: 'common.packageId',
  'potential.base': 'car.prices.basePrice',
  'potential.purchaseGross': 'car.prices.purchasePriceGross',
  'potential.potentialMinAbsolute': 'car.prices.potentialMinAbsolute',
  'potential.potentialMinRelative': 'car.prices.potentialMinRelative',
  'potential.potentialMinMeanAbsolute': 'car.prices.potentialMinMeanAbsolute',
  'potential.potentialMinMeanRelative': 'car.prices.potentialMinMeanRelative',
  'potential.potentialMeanAbsolute': 'car.prices.potentialMeanAbsolute',
  'potential.potentialMeanRelative': 'car.prices.potentialMeanRelative',
  'potential.purchaseNetWholesaleUnrepaired': 'car.prices.purchaseNetWholesaleUnrepaired',
  'potential.purchaseNetRetailRepaired': 'car.prices.purchaseNetRetailRepaired',
  'valuation.price.mean': 'car.prices.marketPrice',
  engineCo2Wltp: 'carsTable.engineCo2Wltp',
  engineCo2Nedc: 'carsTable.engineCo2Nedc',
  'normalized.brand': 'car.brand',
  'normalized.model': 'car.model',
  'normalized.equipmentLine': 'car.equipmentLine',
  'normalized.version': 'car.version',
  'normalized.fullModelName': 'car.fullModelName',
  'bids.bid': 'common.bid',
  'bids.buyingTime': 'bids.buyingTime',
  'bids.biddingTime': 'bids.bidHammers.expired.bidden',
  'bids.user.name': 'common.username',
  'bids.dealer.id': 'navigation.dealer',
  'bids.status': '$t(bids.bidStatus) ($t(configuration.individual))',
};

export const CARS_POTENTIAL_PROPERY_LABELS: Record<string, string> = {
  purchaseGross: 'car.prices.purchasePriceGross',
  potentialMinAbsolute: 'car.prices.potentialMinAbsolute',
  potentialMinRelative: 'car.prices.potentialMinRelative',
  potentialMinMeanAbsolute: 'car.prices.potentialMinMeanAbsolute',
  potentialMinMeanRelative: 'car.prices.potentialMinMeanRelative',
  potentialMeanAbsolute: 'car.prices.potentialMeanAbsolute',
  potentialMeanRelative: 'car.prices.potentialMeanRelative',
  purchaseNetWholesaleUnrepaired: 'car.prices.purchaseNetWholesaleUnrepaired',
  purchaseNetRetailRepaired: 'car.prices.purchaseNetRetailRepaired',
};

export enum CarsPotentialTypes {
  potentialMeanAbsolute = 'potentialMeanAbsolute',
  potentialMeanRelative = 'potentialMeanRelative',
  potentialMinAbsolute = 'potentialMinAbsolute',
  potentialMinMeanAbsolute = 'potentialMinMeanAbsolute',
  potentialMinMeanRelative = 'potentialMinMeanRelative',
  potentialMinRelative = 'potentialMinRelative',
  purchaseGross = 'purchaseGross',
  purchaseNetRetailRepaired = 'purchaseNetRetailRepaired',
  purchaseNetRetailUnrepaired = 'purchaseNetRetailUnrepaired',
}

export const getPropertyLabel = (property: string, forceLanguage?: SupportedLanguage) => {
  const key =
    CARS_BIDS_TABLE_COLUMNS[property as CarTableColumn]?.label || CARS_TABLE_EXPORT_LABELS[property] || property;

  const propertyLabel = forceLanguage ? i18n.getFixedT(forceLanguage)(key) : tr(key);
  if (!propertyLabel) return property;

  return propertyLabel;
};

export const conditionalFormattingTypes: Readonly<Option[]> = [
  { value: ConditionalFormattingType.Model, label: 'car.model' },
  { value: ConditionalFormattingType.FirstRegistration, label: 'car.firstRegistration_lng' },
  { value: ConditionalFormattingType.Mileage, label: 'units.km_brace', options: { target: 'car.mileage' } },
  {
    value: ConditionalFormattingType.NormalizedFuelDetail,
    label: 'carsTableNext.columns.normalized.fuelDetail',
  },
  { value: ConditionalFormattingType.PriceDamage, label: 'units.euro_brace', options: { target: 'car.damage2' } },
  { value: ConditionalFormattingType.DamageType, label: 'car.damageType' },
  { value: ConditionalFormattingType.Nova, label: 'units.percent_brace', options: { target: 'car.nova' } },
  { value: ConditionalFormattingType.NumAuctions, label: 'car.numAuctions' },
  {
    value: ConditionalFormattingType.DateOnlineTimeInMinutes,
    label: 'units.minutes_brace',
    options: { target: 'car.onlineTime' },
  },
  {
    value: ConditionalFormattingType.PotentialBase,
    label: 'units.euro_brace',
    options: { target: 'car.prices.basePrice' },
  },
  {
    value: ConditionalFormattingType.PurchaseNetUnrepaired,
    label: 'units.euro_brace',
    options: { target: 'car.prices.purchaseNetWholesaleUnrepaired' },
  },
  {
    value: ConditionalFormattingType.PurchaseNetRepaired,
    label: 'units.euro_brace',
    options: { target: 'car.prices.purchaseNetRetailRepaired' },
  },
  {
    value: ConditionalFormattingType.PurchaseGross,
    label: 'units.euro_brace',
    options: { target: 'car.prices.purchaseGross' },
  },
  {
    value: ConditionalFormattingType.ValuationMarketPrice,
    label: 'units.euro_brace',
    options: { target: 'car.prices.marketPrice' },
  },
  {
    value: ConditionalFormattingType.PotentialInternetPriceMinAbsolute,
    label: 'units.euro_brace',
    options: { target: 'car.prices.potentialMinAbsolute' },
  },
  {
    value: ConditionalFormattingType.PotentialInternetPriceMinPercent,
    label: 'units.percent_brace',
    options: { target: 'car.prices.potentialMinRelative' },
  },
  {
    value: ConditionalFormattingType.PotentialInternetPriceMinAverageAbsolute,
    label: 'units.euro_brace',
    options: { target: 'car.prices.potentialMinMeanAbsolute' },
  },
  {
    value: ConditionalFormattingType.PotentialInternetPriceMinAveragePercent,
    label: 'units.percent_brace',
    options: { target: 'car.prices.potentialMinMeanRelative' },
  },
  {
    value: ConditionalFormattingType.PotentialInternetPriceAverageAbsolute,
    label: 'units.euro_brace',
    options: { target: 'car.prices.potentialMeanAbsolute' },
  },
  {
    value: ConditionalFormattingType.PotentialInternetPriceAveragePercent,
    label: 'units.percent_brace',
    options: { target: 'car.prices.potentialMeanRelative' },
  },
  {
    value: ConditionalFormattingType.Co2Nefz,
    label: 'units.gram_brace',
    options: { target: 'car.co2Nefz' },
  },
  {
    value: ConditionalFormattingType.Co2Wltp,
    label: 'units.gram_brace',
    options: { target: 'car.co2Wltp' },
  },
  { value: ConditionalFormattingType.TaxType, label: 'carsTable.taxationType' },
  { value: ConditionalFormattingType.Vat, label: 'units.percent_brace', options: { target: 'taxTypes.vat' } },
  { value: ConditionalFormattingType.Eco, label: 'units.euro_brace', options: { target: 'taxTypes.eco' } },
  { value: ConditionalFormattingType.Ecomalus, label: 'units.euro_brace', options: { target: 'taxTypes.ecomalus' } },
  {
    value: ConditionalFormattingType.Transcription,
    label: 'units.euro_brace',
    options: { target: 'taxTypes.transcription' },
  },
  {
    value: ConditionalFormattingType.RegistrationAbsolute,
    label: 'units.euro_brace',
    options: { target: 'taxTypes.registration' },
  },
  {
    value: ConditionalFormattingType.RegistrationRelative,
    label: 'units.percent_brace',
    options: { target: 'taxTypes.registration' },
  },
  {
    value: ConditionalFormattingType.Authentication,
    label: 'units.euro_brace',
    options: { target: 'taxTypes.authentication' },
  },
  {
    value: ConditionalFormattingType.PropertyAcquisition,
    label: 'units.euro_brace',
    options: { target: 'taxTypes.property_acquisition' },
  },
  { value: ConditionalFormattingType.Civil, label: 'units.percent_brace', options: { target: 'taxTypes.civil' } },
  { value: ConditionalFormattingType.Excise, label: 'units.percent_brace', options: { target: 'taxTypes.excise' } },
  { value: ConditionalFormattingType.Promotions, label: 'filterSearch.promotions' },
  { value: ConditionalFormattingType.Highlights, label: 'carsTableNext.filter.equipments' },
];

export const conditionalFormattingColors: Readonly<Option[]> = [
  { value: red[100], label: 'colors.red' },
  { value: orange[100], label: 'colors.orange' },
  { value: yellow[100], label: 'colors.yellow' },
  { value: green[100], label: 'colors.green' },

  { value: pink[100], label: 'colors.pink' },
  { value: purple[100], label: 'colors.purple' },
  { value: deepPurple[100], label: 'colors.deepPurple' },
  { value: indigo[100], label: 'colors.indigo' },
  { value: blue[100], label: 'colors.blue' },
  { value: lightBlue[100], label: 'colors.lightBlue' },
  { value: cyan[100], label: 'colors.cyan' },
  { value: teal[100], label: 'colors.teal' },
  { value: lightGreen[100], label: 'colors.lightGreen' },
  { value: lime[100], label: 'colors.lime' },
  { value: amber[100], label: 'colors.amber' },
  { value: deepOrange[100], label: 'colors.deepOrange' },
  { value: brown[100], label: 'colors.brown' },
  { value: grey[100], label: 'colors.grey' },
  { value: blueGrey[100], label: 'colors.blueGrey' },
];

export const REGIONS_LABELS: Readonly<{ value: ValuationCountryCode; label: string }[]> = [
  { value: ValuationCountryCode.Fr, label: 'regions.FR' },
  { value: ValuationCountryCode.At, label: 'regions.AT' },
  { value: ValuationCountryCode.AtLot, label: 'regions.AT-LOT' },
];

// Zwischenlösung solange es AT_Lot und AT_CC gbit, ENUM aus BE nicht änderbar
export const SHORT_REGIONS_LABELS: Readonly<{ value: ValuationCountryCode; label: string }[]> = [
  { value: ValuationCountryCode.Fr, label: 'FR' },
  { value: ValuationCountryCode.AtLot, label: 'AT' },
  { value: ValuationCountryCode.At, label: 'AT_CC' },
  { value: ValuationCountryCode.De, label: 'DE' },
];

export const TAXTYPES: Readonly<{ value: TaxType; label: string }[]> = [
  { value: TaxType.Regelbesteuert, label: 'carsTable.regularilyTaxed' },
  { value: TaxType.Differenzbesteuert, label: 'carsTable.differentialTaxation' },
];

export const countryCodeToOption = (countryCode: string, lang: string): Option => ({
  value: countryCode,
  label:
    countries.getName(countryCode, lang) === countryCode
      ? getLabel(REGIONS_LABELS, countryCode)
      : countries.getName(countryCode, lang),
});

export const countryCodeToRegion = (countryCode: string, lang: string): Option => ({
  value: countryCode,
  label:
    getLabel(REGIONS_LABELS, countryCode) === countryCode
      ? countries.getName(countryCode, lang)
      : getLabel(REGIONS_LABELS, countryCode),
});

export const countryCodeToOptions = (countryCodes: Array<string>, lang: string): Option[] =>
  countryCodes.map((countryCode) => countryCodeToOption(countryCode, lang));

export const countryCodeToRegions = (countryCodes: Array<string>, lang: string): Option[] =>
  countryCodes.map((countryCode) => countryCodeToRegion(countryCode, lang));

export const getRegionOptions = () => [
  { value: 'default', label: 'Global' },
  ...countryCodeToRegions(SUPPORTED_COUNTRIES, 'de'),
];

export const USERNAME_STATUS_LABELS: Readonly<{ value: UsernameStatusBasic; label: string }[]> = [
  { value: UsernameStatusBasic.Disabled, label: 'common.inactive' },
  { value: UsernameStatusBasic.Pending, label: 'common.pending' },
  { value: UsernameStatusBasic.Enabled, label: 'common.active' },
];

export const TERMS_AND_CONDITIONS_LABELS: Readonly<Option[]> = [
  {
    value: TermsConditionsType.VwfsTermsConditions,
    label: 'termsAndConditions.vwfs_suffix',
    options: { suffix: 'termsAndConditions.termsAndConditions' },
  },
  {
    value: TermsConditionsType.TermsConditions,
    label: 'termsAndConditions.iris_suffix',
    options: { suffix: 'termsAndConditions.termsAndConditions' },
  },
  {
    value: TermsConditionsType.VwfsPrivacy,
    label: 'termsAndConditions.vwfs_suffix',
    options: { suffix: 'termsAndConditions.privacy' },
  },
];
export type PriceStrategyType =
  PriceType.PotentialMeanRelative | PriceType.PotentialMinMeanRelative | PriceType.PotentialMinRelative;

export const TRANSPORTATION_SELECTION_TYPE_LABELS: Record<TransportationSelectionType, string> = {
  [TransportationSelectionType.Unknown]: 'common.unknown',
  [TransportationSelectionType.Pickup]: 'purchaseAgent.form.transportationSelectionType.PICKUP',
  [TransportationSelectionType.DeliveryWithLowestPrice]:
    'purchaseAgent.form.transportationSelectionType.DELIVERY_WITH_LOWEST_PRICE',
};

export const PRICE_STRATEGY_LABEL: Record<PriceStrategyType, string> = {
  [PriceType.PotentialMeanRelative]: 'carsTable.priceStrategies.meanMarket',
  [PriceType.PotentialMinMeanRelative]: 'carsTable.priceStrategies.minMeanMarket',
  [PriceType.PotentialMinRelative]: 'carsTable.priceStrategies.minMarket',
};

export const recordsToOptions = (records: Record<any, string>): Option[] =>
  Object.entries(records).map(([key, value]) => ({
    label: value,
    value: key,
  }));

export const formattingScopes: Option[] = [
  { value: ConditionalFormattingScope.Cell, label: 'conditionalFormatting.scope.cell' },
  { value: ConditionalFormattingScope.Row, label: 'conditionalFormatting.scope.row' },
];

export const getRecommendationReasonText = (reason?: ReasonDto): string | undefined => {
  const { totalVehicleCount, soldVehicleCount, soldAvgTurnoverDays } = reason ?? {};
  if (totalVehicleCount == null || soldVehicleCount == null || soldAvgTurnoverDays == null) {
    return undefined;
  }
  return tr('recommendationsPage.reasonText', {
    totalCount: totalVehicleCount,
    soldCount: soldVehicleCount,
    avgDays: new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }).format(soldAvgTurnoverDays),
  });
};

export const formatDeliveryAddress = (deliveryAddress: DeliveryAddress | DeliveryAddressResponse) => {
  if (deliveryAddress.type === DeliveryAddressType.PlatformStandard) {
    return tr('addressCard.platformStandard.title');
  }
  const location = deliveryAddress.street || [deliveryAddress.zip, deliveryAddress.city].filter(Boolean).join(' ');
  return deliveryAddress.betriebsId ? `${location} (${deliveryAddress.betriebsId})` : location;
};

export const bidVisibilityTypes: Option<BidVisibility>[] = [
  { value: BidVisibility.User, label: 'bids.user' },
  { value: BidVisibility.Dealer, label: 'bids.retailer' },
  { value: BidVisibility.Country, label: 'contact.country' },
  { value: BidVisibility.All, label: 'common.all' },
];

export const biddingTimeTypes: Option<DateFilter>[] = [
  { value: DateFilter.All, label: 'common.all' },
  { value: DateFilter.ThisMonth, label: 'carsTable.thisMonth' },
  { value: DateFilter.LastMonth, label: 'carsTable.lastMonth' },
  { value: DateFilter.Individual, label: 'configuration.individual' },
  { value: DateFilter.Today, label: 'carsTable.today' },
  { value: DateFilter.ThisWeek, label: 'carsTable.thisWeek' },
];

const getProviderLabel = (provider: TransportationProvider): string => {
  if (PROVIDERS_SHOWN_AS_VIA_ALLMOBIL.includes(provider)) {
    return 'transportation.provider.transportViaAllmobil';
  }

  // Define other label mappings here
  switch (provider) {
    case TransportationProvider.PlatformDefault:
      return 'transportation.provider.platformDefault';
    case TransportationProvider.VwfsFirstCallPickup:
    case TransportationProvider.VwfsPickup:
      return 'transportation.provider.pickup';
    case TransportationProvider.VwfsTransport:
      return 'transportation.provider.vwfsTransport';
    case TransportationProvider.PhsTransshipment:
      return 'transportation.provider.phsTransshipment';
    case TransportationProvider.VwfsAxisTransport:
      return 'transportation.provider.vwfsAxisTransport';
    case TransportationProvider.TransportingWheelsTransport:
    case TransportationProvider.RhTransport:
    case TransportationProvider.AxisTransport:
    case TransportationProvider.MosolfTransport:
    default:
      return 'transportation.provider.transportingUnknown';
  }
};

export const transportOptionProvider: Option<TransportationProvider>[] = [
  { value: TransportationProvider.PhsTransshipment, label: getProviderLabel(TransportationProvider.PhsTransshipment) },
  { value: TransportationProvider.PlatformDefault, label: getProviderLabel(TransportationProvider.PlatformDefault) },
  {
    value: TransportationProvider.TransportingWheelsTransport,
    label: getProviderLabel(TransportationProvider.TransportingWheelsTransport),
  },
  {
    value: TransportationProvider.VwfsFirstCallPickup,
    label: getProviderLabel(TransportationProvider.VwfsFirstCallPickup),
  },
  {
    value: TransportationProvider.VwfsPickup,
    label: getProviderLabel(TransportationProvider.VwfsPickup),
  },
  {
    value: TransportationProvider.VwfsAllmobilTransport,
    label: getProviderLabel(TransportationProvider.VwfsAllmobilTransport),
  },
  { value: TransportationProvider.VwfsTransport, label: getProviderLabel(TransportationProvider.VwfsTransport) },
  {
    value: TransportationProvider.VwfsTransportingWheelsTransport,
    label: getProviderLabel(TransportationProvider.VwfsTransportingWheelsTransport),
  },
  {
    value: TransportationProvider.RhTransport,
    label: getProviderLabel(TransportationProvider.RhTransport),
  },
  {
    value: TransportationProvider.VwfsRhTransport,
    label: getProviderLabel(TransportationProvider.VwfsRhTransport),
  },
  {
    value: TransportationProvider.AxisTransport,
    label: getProviderLabel(TransportationProvider.AxisTransport),
  },
  {
    value: TransportationProvider.VwfsAxisTransport,
    label: getProviderLabel(TransportationProvider.VwfsAxisTransport),
  },
  {
    value: TransportationProvider.VwfsMosolfTransport,
    label: getProviderLabel(TransportationProvider.VwfsMosolfTransport),
  },
  {
    value: TransportationProvider.MosolfTransport,
    label: getProviderLabel(TransportationProvider.MosolfTransport),
  },
];

export const transportOptionStatus: Option<TransportationState>[] = [
  { value: TransportationState.Commissioned, label: 'transportation.status.commissioned' },
  { value: TransportationState.NotSent, label: 'transportation.status.notSent' },
  {
    value: TransportationState.Error,
    label: 'transportation.status.error',
  },
  {
    value: TransportationState.WaitingForValidSourceAddress,
    label: 'transportation.status.waitingForValidSourceAddress',
  },
  {
    value: TransportationState.Aborted,
    label: 'transportation.status.aborted',
  },
  {
    value: TransportationState.WaitingForValidSourceAddressFromVwfs,
    label: 'transportation.status.waitingForValidSourceAddressFromVwfs',
  },
];

export const transportDeliveryStatus: Option<DeliveryStatus>[] = [
  { value: DeliveryStatus.Planning, label: 'transportation.deliveryStatus.planning' },
  { value: DeliveryStatus.Planned, label: 'transportation.deliveryStatus.planned' },
  { value: DeliveryStatus.PickedUp, label: 'transportation.deliveryStatus.pickedUp' },
  { value: DeliveryStatus.Delivered, label: 'transportation.deliveryStatus.delivered' },
  { value: DeliveryStatus.Cancelled, label: 'transportation.deliveryStatus.cancelled' },
  { value: DeliveryStatus.OnHold, label: 'transportation.deliveryStatus.onHold' },
];
